/**
 * FormBuilderPreview Component
 *
 * Renders a live preview of the form using the DynamicForm component.
 * Shows how the form will look and function when deployed.
 */

import { forwardRef, memo, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Text,
  Group,
  SegmentedControl,
  ActionIcon,
  Tooltip,
  Stack,
  Divider,
  Code,
  ScrollArea,
} from '@mantine/core';
import { Icon } from '@/components/ui/icons/Icon';
import { DynamicForm } from '@/components/advanced/forms';
import {
  useFormBuilderStore,
  selectForm,
  selectFormSettings,
} from '@/stores/formBuilderStore';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import { toFormFieldConfigs, getInitialValues, isLayoutElement } from './utils';
import { FIELD_TYPE_CONFIGS } from './constants';
import type { FormBuilderPreviewProps, FormFieldDefinition } from './types';
import classes from './FormBuilder.module.css';

type ViewportSize = 'desktop' | 'tablet' | 'mobile';

const VIEWPORT_WIDTHS: Record<ViewportSize, number> = {
  desktop: 900,
  tablet: 768,
  mobile: 375,
};

/**
 * FormBuilderPreview Component
 */
const FormBuilderPreview = forwardRef<HTMLDivElement, FormBuilderPreviewProps>(
  ({ className }, ref) => {
    const form = useFormBuilderStore(selectForm);
    const settings = useFormBuilderStore(selectFormSettings);
    const { togglePreview } = useFormBuilderStore();

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [viewport, setViewport] = useState<ViewportSize>('desktop');
    const [showSubmittedData, setShowSubmittedData] = useState(false);
    const [submittedData, setSubmittedData] = useState<Record<string, unknown> | null>(null);

    // Convert form fields to DynamicForm config
    const formFields = useMemo(() => {
      if (!form) return [];
      return toFormFieldConfigs(form);
    }, [form]);

    // Get initial values
    const initialValues = useMemo(() => {
      if (!form) return {};
      return getInitialValues(form);
    }, [form]);

    const handleSubmit = useCallback((values: Record<string, unknown>) => {
      setSubmittedData(values);
      setShowSubmittedData(true);
    }, []);

    const handleReset = useCallback(() => {
      setSubmittedData(null);
      setShowSubmittedData(false);
    }, []);

    if (!form) {
      return (
        <Box ref={ref} className={`${classes.previewContainer} ${className || ''}`}>
          <Text ta="center" c="dimmed" py="xl" style={accessibilityStyles}>
            No form to preview
          </Text>
        </Box>
      );
    }

    return (
      <Box ref={ref} className={`${classes.previewContainer} ${className || ''}`}>
        {/* Preview header */}
        <Paper withBorder p="md" mb="lg">
          <Group justify="space-between">
            <Group gap="sm">
              <Icon name="eye" size={20} />
              <Text fw={600} style={accessibilityStyles}>Form Preview</Text>
            </Group>

            <Group gap="md">
              {/* Viewport selector */}
              <Box className={classes.previewViewport}>
                <SegmentedControl
                  size="xs"
                  value={viewport}
                  onChange={(v) => setViewport(v as ViewportSize)}
                  data={[
                    {
                      value: 'desktop',
                      label: (
                        <Tooltip label="Desktop (900px)">
                          <Icon name="monitor" size={16} />
                        </Tooltip>
                      ),
                    },
                    {
                      value: 'tablet',
                      label: (
                        <Tooltip label="Tablet (768px)">
                          <Icon name="tablet" size={16} />
                        </Tooltip>
                      ),
                    },
                    {
                      value: 'mobile',
                      label: (
                        <Tooltip label="Mobile (375px)">
                          <Icon name="smartphone" size={16} />
                        </Tooltip>
                      ),
                    },
                  ]}
                />
              </Box>

              <Tooltip label="Close preview">
                <ActionIcon variant="subtle" onClick={togglePreview}>
                  <Icon name="x" size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </Paper>

        {/* Preview content */}
        <Box
          style={{
            maxWidth: VIEWPORT_WIDTHS[viewport],
            margin: '0 auto',
            transition: 'max-width 0.2s ease',
          }}
        >
          <Paper className={classes.previewForm} withBorder>
            {/* Form header */}
            <Box className={classes.previewHeader}>
              <Text size="xl" fw={700} mb="xs" style={accessibilityStyles}>
                {form.name}
              </Text>
              {form.description && (
                <Text c="dimmed" size="sm" style={accessibilityStyles}>
                  {form.description}
                </Text>
              )}
            </Box>

            {/* Layout elements need to be rendered separately */}
            {form.fields.some((f) => isLayoutElement(f.type)) ? (
              <FormWithLayoutElements
                form={form}
                settings={settings}
                onSubmit={handleSubmit}
              />
            ) : (
              <DynamicForm
                fields={formFields}
                initialValues={initialValues}
                onSubmit={handleSubmit}
                columns={settings.columns}
                gap={settings.gap}
                submitLabel={settings.submitButtonText}
                showReset={settings.showResetButton}
                compact={settings.compact}
                gradientVariant={settings.gradientVariant}
              />
            )}
          </Paper>

          {/* Submitted data display */}
          {showSubmittedData && submittedData && (
            <Paper withBorder p="md" mt="lg">
              <Group justify="space-between" mb="sm">
                <Group gap="sm">
                  <Icon name="checkCircle" size={20} color="green" />
                  <Text fw={600} style={accessibilityStyles}>Form Submitted</Text>
                </Group>
                <ActionIcon variant="subtle" onClick={handleReset}>
                  <Icon name="x" size={16} />
                </ActionIcon>
              </Group>
              <Divider mb="sm" />
              <ScrollArea h={200}>
                <Code block>
                  {JSON.stringify(submittedData, null, 2)}
                </Code>
              </ScrollArea>
            </Paper>
          )}
        </Box>
      </Box>
    );
  }
);

FormBuilderPreview.displayName = 'FormBuilderPreview';

/**
 * Form renderer that handles layout elements mixed with form fields
 */
const FormWithLayoutElements = memo(
  ({
    form,
    settings,
    onSubmit,
  }: {
    form: NonNullable<ReturnType<typeof selectForm>>;
    settings: ReturnType<typeof selectFormSettings>;
    onSubmit: (values: Record<string, unknown>) => void;
  }) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [values, setValues] = useState<Record<string, unknown>>(() =>
      getInitialValues(form)
    );
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const handleChange = useCallback((name: string, value: unknown) => {
      setValues((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleBlur = useCallback((name: string) => {
      setTouched((prev) => ({ ...prev, [name]: true }));
    }, []);

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(values);
      },
      [values, onSubmit]
    );

    const renderLayoutElement = (field: FormFieldDefinition) => {
      switch (field.type) {
        case 'heading':
          return (
            <Text
              size={
                field.headingLevel === 1
                  ? 'xl'
                  : field.headingLevel === 2
                  ? 'lg'
                  : 'md'
              }
              fw={600}
              mt="md"
              mb="sm"
              style={accessibilityStyles}
            >
              {field.label}
            </Text>
          );
        case 'paragraph':
          return (
            <Text size="sm" c="dimmed" mb="sm" style={accessibilityStyles}>
              {field.content || field.label}
            </Text>
          );
        case 'divider':
          return <Divider my="md" />;
        case 'spacer':
          return <Box style={{ height: field.spacerHeight || 24 }} />;
        default:
          return null;
      }
    };

    // Group consecutive form fields for DynamicForm rendering
    const fieldGroups: { type: 'layout' | 'fields'; items: FormFieldDefinition[] }[] = [];
    let currentGroup: FormFieldDefinition[] = [];
    let currentType: 'layout' | 'fields' | null = null;

    form.fields.forEach((field) => {
      const isLayout = isLayoutElement(field.type);
      const groupType = isLayout ? 'layout' : 'fields';

      if (currentType === null) {
        currentType = groupType;
        currentGroup = [field];
      } else if (currentType === groupType) {
        currentGroup.push(field);
      } else {
        fieldGroups.push({ type: currentType, items: currentGroup });
        currentType = groupType;
        currentGroup = [field];
      }
    });

    if (currentGroup.length > 0 && currentType) {
      fieldGroups.push({ type: currentType, items: currentGroup });
    }

    return (
      <form onSubmit={handleSubmit}>
        <Stack gap="xs">
          {fieldGroups.map((group, groupIndex) => {
            if (group.type === 'layout') {
              return (
                <Box key={groupIndex}>
                  {group.items.map((field) => (
                    <Box
                      key={field.id}
                      style={{
                        gridColumn: field.colSpan ? `span ${field.colSpan}` : undefined,
                      }}
                    >
                      {renderLayoutElement(field)}
                    </Box>
                  ))}
                </Box>
              );
            }

            // Render form fields group using DynamicForm
            const formFields = toFormFieldConfigs({ ...form, fields: group.items });
            return (
              <DynamicForm
                key={groupIndex}
                fields={formFields}
                initialValues={values}
                onSubmit={() => {}}
                columns={settings.columns}
                gap={settings.gap}
                compact={settings.compact}
                gradientVariant={settings.gradientVariant}
                submitLabel=""
              />
            );
          })}
        </Stack>

        {/* Submit buttons are handled in the last DynamicForm or need separate rendering */}
        <Group
          justify={
            settings.submitButtonPosition === 'left'
              ? 'flex-start'
              : settings.submitButtonPosition === 'center'
              ? 'center'
              : settings.submitButtonPosition === 'stretch'
              ? 'stretch'
              : 'flex-end'
          }
          mt="xl"
        >
          {settings.showResetButton && (
            <button type="reset" className="mantine-Button-root mantine-Button-subtle">
              {settings.resetButtonText}
            </button>
          )}
          <button type="submit" className="mantine-Button-root">
            {settings.submitButtonText}
          </button>
        </Group>
      </form>
    );
  }
);

FormWithLayoutElements.displayName = 'FormWithLayoutElements';

export default memo(FormBuilderPreview);
